import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ExportOptions = ({ 
  onExport, 
  isExporting = false,
  resumeContent = '',
  matchScore = 0 
}) => {
  const [selectedFormat, setSelectedFormat] = useState('pdf');
  const [selectedTemplate, setSelectedTemplate] = useState('modern');
  const [exportSettings, setExportSettings] = useState({
    includeKeywords: true,
    atsOptimized: true,
    includeMatchScore: false,
    customFileName: ''
  });

  const formats = [
    {
      id: 'pdf',
      name: 'PDF',
      description: 'Best for job applications',
      icon: 'FileText',
      recommended: true
    },
    {
      id: 'docx',
      name: 'Word Document',
      description: 'Editable format',
      icon: 'FileEdit'
    },
    {
      id: 'txt',
      name: 'Plain Text',
      description: 'ATS-friendly format',
      icon: 'File'
    }
  ];

  const templates = [
    {
      id: 'modern',
      name: 'Modern Professional',
      description: 'Clean design with subtle colors',
      preview: '/assets/templates/modern-preview.jpg'
    },
    {
      id: 'classic',
      name: 'Classic Traditional',
      description: 'Traditional black and white layout',
      preview: '/assets/templates/classic-preview.jpg'
    },
    {
      id: 'minimal',
      name: 'Minimal Clean',
      description: 'Simple and elegant design',
      preview: '/assets/templates/minimal-preview.jpg'
    },
    {
      id: 'ats',
      name: 'ATS Optimized',
      description: 'Maximum compatibility with ATS systems',
      preview: '/assets/templates/ats-preview.jpg',
      recommended: true
    }
  ];

  const handleExport = () => {
    const exportData = {
      format: selectedFormat,
      template: selectedTemplate,
      content: resumeContent,
      settings: exportSettings,
      fileName: exportSettings?.customFileName || `resume_optimized_${Date.now()}`
    };
    onExport(exportData);
  };

  const handleSettingChange = (setting, value) => {
    setExportSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border bg-muted/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon name="Download" size={20} className="text-primary" />
            <h3 className="font-semibold text-card-foreground">Export Resume</h3>
          </div>
          <div className="flex items-center space-x-2 text-sm">
            <Icon name="TrendingUp" size={16} className="text-success" />
            <span className="text-success font-medium">{matchScore}% optimized</span>
          </div>
        </div>
      </div>
      <div className="p-4 space-y-6">
        {/* Format Selection */}
        <div>
          <h4 className="font-medium text-card-foreground mb-3">Export Format</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {formats?.map((format) => (
              <button
                key={format?.id}
                onClick={() => setSelectedFormat(format?.id)}
                className={`relative p-4 border rounded-lg text-left transition-all duration-200 ${
                  selectedFormat === format?.id
                    ? 'border-primary bg-primary/5 ring-2 ring-primary/20' :'border-border hover:border-primary/50 hover:bg-muted/30'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <Icon 
                    name={format?.icon} 
                    size={20} 
                    className={selectedFormat === format?.id ? 'text-primary' : 'text-muted-foreground'} 
                  />
                  <div className="flex-1">
                    <div className="font-medium text-card-foreground text-sm">
                      {format?.name}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {format?.description}
                    </div>
                  </div>
                </div>
                {format?.recommended && (
                  <div className="absolute top-2 right-2">
                    <span className="px-2 py-0.5 bg-success/10 text-success text-xs rounded-full">
                      Recommended
                    </span>
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Template Selection */}
        <div>
          <h4 className="font-medium text-card-foreground mb-3">Resume Template</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {templates?.map((template) => (
              <button
                key={template?.id}
                onClick={() => setSelectedTemplate(template?.id)}
                className={`relative p-4 border rounded-lg text-left transition-all duration-200 ${
                  selectedTemplate === template?.id
                    ? 'border-primary bg-primary/5 ring-2 ring-primary/20' :'border-border hover:border-primary/50 hover:bg-muted/30'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className="w-12 h-16 bg-muted rounded border flex-shrink-0 flex items-center justify-center">
                    <Icon name="FileText" size={16} className="text-muted-foreground" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-card-foreground text-sm">
                      {template?.name}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {template?.description}
                    </div>
                  </div>
                </div>
                {template?.recommended && (
                  <div className="absolute top-2 right-2">
                    <span className="px-2 py-0.5 bg-success/10 text-success text-xs rounded-full">
                      Recommended
                    </span>
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Export Settings */}
        <div>
          <h4 className="font-medium text-card-foreground mb-3">Export Settings</h4>
          <div className="space-y-3">
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={exportSettings?.includeKeywords}
                onChange={(e) => handleSettingChange('includeKeywords', e?.target?.checked)}
                className="w-4 h-4 text-primary border-border rounded focus:ring-primary focus:ring-2"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-card-foreground">Include optimized keywords</div>
                <div className="text-xs text-muted-foreground">Embed relevant keywords for better ATS scanning</div>
              </div>
            </label>

            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={exportSettings?.atsOptimized}
                onChange={(e) => handleSettingChange('atsOptimized', e?.target?.checked)}
                className="w-4 h-4 text-primary border-border rounded focus:ring-primary focus:ring-2"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-card-foreground">ATS optimization</div>
                <div className="text-xs text-muted-foreground">Format for maximum compatibility with ATS systems</div>
              </div>
            </label>

            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={exportSettings?.includeMatchScore}
                onChange={(e) => handleSettingChange('includeMatchScore', e?.target?.checked)}
                className="w-4 h-4 text-primary border-border rounded focus:ring-primary focus:ring-2"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-card-foreground">Include match score</div>
                <div className="text-xs text-muted-foreground">Add optimization score as a comment (for your reference)</div>
              </div>
            </label>
          </div>
        </div>

        {/* Custom File Name */}
        <div>
          <label className="block text-sm font-medium text-card-foreground mb-2">
            Custom File Name (Optional)
          </label>
          <input
            type="text"
            value={exportSettings?.customFileName}
            onChange={(e) => handleSettingChange('customFileName', e?.target?.value)}
            placeholder="e.g., John_Doe_Frontend_Developer_Resume"
            className="w-full px-3 py-2 border border-border rounded-md bg-background text-card-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          <div className="text-xs text-muted-foreground mt-1">
            Leave empty for auto-generated name
          </div>
        </div>

        {/* Export Preview */}
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-3">
            <Icon name="Eye" size={16} className="text-primary" />
            <span className="text-sm font-medium text-card-foreground">Export Preview</span>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Format:</span>
              <span className="text-card-foreground font-medium">{formats?.find(f => f?.id === selectedFormat)?.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Template:</span>
              <span className="text-card-foreground font-medium">{templates?.find(t => t?.id === selectedTemplate)?.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">File name:</span>
              <span className="text-card-foreground font-medium font-mono text-xs">
                {exportSettings?.customFileName || `resume_optimized_${Date.now()}`}.{selectedFormat}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Estimated size:</span>
              <span className="text-card-foreground font-medium">~{Math.ceil(resumeContent?.length / 1024)}KB</span>
            </div>
          </div>
        </div>

        {/* Export Button */}
        <Button
          variant="default"
          size="lg"
          fullWidth
          loading={isExporting}
          iconName="Download"
          iconPosition="left"
          onClick={handleExport}
          disabled={!resumeContent?.trim()}
        >
          {isExporting ? 'Exporting Resume...' : 'Export Optimized Resume'}
        </Button>

        {/* Additional Options */}
        <div className="flex items-center justify-center space-x-4 text-sm">
          <button className="flex items-center space-x-1 text-primary hover:text-primary/80 transition-colors duration-200">
            <Icon name="Share2" size={14} />
            <span>Share Link</span>
          </button>
          <span className="text-muted-foreground">•</span>
          <button className="flex items-center space-x-1 text-primary hover:text-primary/80 transition-colors duration-200">
            <Icon name="Mail" size={14} />
            <span>Email Resume</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportOptions;